/**
 * Kalibrierung: die Notbremse, wenn die automatische Geraeteerkennung danebenliegt.
 *
 * Die App misst Strecken in echten Zentimetern. Dafuer muss sie wissen, wie
 * gross ein Pixel physikalisch ist - und das verraet kein Browser. Erkennt sie
 * das Geraet nicht sicher, schaetzt sie. Hier kann der Nutzer nachmessen und aus
 * der Schaetzung eine Messung machen.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { useDevice } from '../DeviceContext'
import {
  cardShortSidePx,
  clearCalibration,
  confidenceLabel,
  ppiFromCardShortSide,
  REFERENCE_CARD_LONG_CM,
  REFERENCE_CARD_SHORT_CM,
  saveCalibration,
} from '../engine/dpi'

/** Seitenverhaeltnis einer ID-1-Karte, hochkant gelegt. */
const CARD_ASPECT = REFERENCE_CARD_LONG_CM / REFERENCE_CARD_SHORT_CM

export function KalibrierenScreen() {
  const { metrics, refresh } = useDevice()
  const navigate = useNavigate()
  const wrapRef = useRef<HTMLDivElement>(null)

  const [widthPx, setWidthPx] = useState(() => cardShortSidePx(metrics.ppi, metrics.dpr))
  const [maxWidth, setMaxWidth] = useState(320)
  const [saved, setSaved] = useState(false)

  // Der Regler darf nur so weit gehen, wie das Rechteck auch hinpasst. Ein
  // ueber den Rand hinaus gezogenes Rechteck waere nicht mehr abzugleichen.
  useEffect(() => {
    const update = (): void => {
      const available = (wrapRef.current?.clientWidth ?? window.innerWidth) - 16
      setMaxWidth(Math.max(140, available))
      setWidthPx((current) => Math.min(current, Math.max(140, available)))
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const resultingPpi = ppiFromCardShortSide(widthPx, metrics.dpr)

  async function handleSave(): Promise<void> {
    saveCalibration(resultingPpi)
    await refresh()
    setSaved(true)
  }

  async function handleReset(): Promise<void> {
    clearCalibration()
    await refresh()
    setSaved(false)
    navigate('/')
  }

  return (
    <div className="screen screen--page">
      <Header title="Gerät kalibrieren" />
      <div className="page" ref={wrapRef}>
        <p className="page__lead">
          Leg eine Bankkarte, den Führerschein oder den Personalausweis hochkant auf das Rechteck und
          zieh den Regler, bis beide genau gleich groß sind.
        </p>
        <p className="page__note">
          Alle diese Karten haben weltweit exakt dasselbe Format ({REFERENCE_CARD_LONG_CM} ×{' '}
          {REFERENCE_CARD_SHORT_CM} cm nach ISO&nbsp;7810). Deshalb taugen sie als Maßstab.
          Freiwillig – ohne Kalibrierung zählt dein Ergebnis genauso, es ist nur weniger genau.
        </p>

        <div className="calib">
          <div
            className="calib__card"
            style={{ width: `${widthPx}px`, height: `${widthPx * CARD_ASPECT}px` }}
          >
            <span className="calib__chip" />
          </div>
        </div>

        <input
          className="calib__slider"
          type="range"
          min={140}
          max={maxWidth}
          step={0.5}
          value={widthPx}
          onChange={(event) => {
            setWidthPx(Number(event.target.value))
            setSaved(false)
          }}
          aria-label="Größe des Rechtecks anpassen"
        />

        <dl className="facts">
          <div>
            <dt>Ergibt</dt>
            <dd>{Math.round(resultingPpi)} ppi</dd>
          </div>
          <div>
            <dt>Bisher</dt>
            <dd>
              {Math.round(metrics.ppi)} ppi ({confidenceLabel(metrics.confidence)})
            </dd>
          </div>
          <div>
            <dt>Abweichung</dt>
            <dd>{deviationLabel(resultingPpi, metrics.ppi)}</dd>
          </div>
        </dl>

        <div className="result__actions">
          <button className="button button--primary" type="button" onClick={() => void handleSave()}>
            Übernehmen
          </button>
          <button className="button" type="button" onClick={() => void handleReset()}>
            Automatik zurück
          </button>
        </div>

        {saved && (
          <p className="message message--ok">
            Gespeichert. Ab jetzt sind deine Zentimeter echte Zentimeter – und deine Ergebnisse
            zählen in der weltweiten Bestenliste.
          </p>
        )}
      </div>
    </div>
  )
}

function deviationLabel(candidate: number, current: number): string {
  if (current <= 0) return '–'
  const percent = ((candidate - current) / current) * 100
  const sign = percent > 0 ? '+' : ''
  return `${sign}${percent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`
}
